/**
 * @fileoverview Hook for handling UIN (Unique Identification Number) validation workflow for orders
 * Manages the state and logic for validating UINs across multiple divisions
 */

import jwtDecode from "jwt-decode";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Button, Input, Modal } from "src/components";
import { getCookieValue } from "src/constants/functions";
import UinOrderDetailsPopup from "src/container/uin-order-details-popup.component";
import { RootState } from "src/context/store";
import { useToast } from "src/hooks/useToast";
import { EditDivision, getIndphUinValidationDetails, validateUin } from "src/services/counter-api";
import { queryEmployeeDetails } from "src/services/employee-api";
import { getUinRequired, updateIndphUinValidation } from "src/services/order-api";

/**
 * Interface representing the UIN validation status for a division
 */
interface DivisionUINStatus {
  divisionCode: string;
  positionCode: string;
  isVerified: boolean;
  isSkipped: boolean;
  psrUCode: string;
  requiresUIN: boolean;
  allowsAlternateUIN: boolean;
  associatedCounterName: string;
  associatedCounterMobileNumber: string;
  existingAssociatedCounterName: string;
  existingAssociatedCounterMobileNumber: string;
  existingUinCode: string;
  uin_code: string;
  divisionName: string;
  divisionData: any; // TODO: Type this properly
  positionCodeName: string;
  employeeName: string;
  employeeShortCode: string;
}

/**
 * Hook for managing UIN validation workflow
 * @param divisionCode - Array of division codes that need UIN validation
 * @param counterCode - Code identifying the counter
 * @param counterLocationCode - Location code for the counter
 * @param onUinVerified - Callback function to execute when all UINs are verified
 */
const useOrderUinConfirmation = ({
  divisionCode,
  counterCode,
  // counterLocationCode,
  onUinVerified,
  orderCode,
  divisionProductsData,
  isApproval,
}: {
  divisionCode: { counterDivisionCode: number; expired: boolean }[];
  counterCode: string;
  counterLocationCode: string;
  onUinVerified: () => void;
  orderCode: string;
  divisionProductsData: any[];
  isApproval: boolean;
}) => {
  // State management for UIN validation workflow
  const [isOpen, setIsOpen] = useState(false);
  const [currentDivisionIndex, setCurrentDivisionIndex] = useState(0);
  const [divisionStatuses, setDivisionStatuses] = useState<DivisionUINStatus[]>([]);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [uinInput, setUinInput] = useState("");
  const [alternateUINInput, setAlternateUINInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingUIN, setIsVerifyingUIN] = useState(false);
  const [isUpdatingDivision, setIsUpdatingDivision] = useState(false);
  const [showValidationResponse, setShowValidationResponse] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [associatedCounter, setAssociatedCounter] = useState("");
  const [isErrorAssociatedCounter, setIsErrorAssociatedCounter] = useState(false);
  const [isErrorMobileNumber, setIsErrorMobileNumber] = useState(false);

  // Get counter data from Redux store
  const { selectedCounterData, selectedCounterDataForUin } = useSelector((state: RootState) => state.ord);
  const modifiedCounterData = {
    counter_id: selectedCounterData?.counter_location?.counter_hdr_id?.counter_id,
    counter_company_location_id: selectedCounterData?.counter_company_location_id,
    drph_lnk_type: selectedCounterData?.drph_lnk_type,
    counter_location: selectedCounterData?.counter_location,
    company_data: {
      code: selectedCounterData?.company_code?.code,
      name: selectedCounterData?.company_code?.name,
    },
    counter_field_force_id: selectedCounterData?.counter_field_force_id,
  }

  const counterData = isApproval ? { counter_id: selectedCounterDataForUin?.[0]?.counter_location?.counter_hdr_id?.counter_id, ...selectedCounterDataForUin?.[0] } : modifiedCounterData;

  const { toast } = useToast();
  /**
   * Initiates UIN validation process for all divisions 
   * @returns {Promise<boolean>} True if validation is complete or not needed, false if validation is pending
   */

  // const checkUinRequiredForDivisions = async () => {
  //   const response = await getUinRequired(divisionCode, selectedCounterData?.counter_location?.counter_hdr_id?.counter_code);
  //   setValidatedDivisions(response?.data?.data);
  // };
  const checkUINValidation = async () => {
    window.sessionStorage.setItem("doctor_counter_id", counterData?.counter_id);
    if (!divisionCode?.length) {
      // If no divisions require UIN validation, proceed
      onUinVerified();
      return true;
    }
    let response;
    let updatedDivisionCode: any[] = [];
    if (arrayOfLinkTypesForAssociatedCounter.includes(counterData?.drph_lnk_type?.dr_ph_lnk_code)) {
      const codes = divisionCode?.map((item) => item?.counterDivisionCode?.toString());
      response = await getUinRequired(codes, counterData?.counter_location?.counter_hdr_id?.counter_code);

      updatedDivisionCode = await Promise.all(
        divisionCode.map(async (item) => {
          const response = await getIndphUinValidationDetails({
            counter_code: counterCode,
            company_divisioncode: item.counterDivisionCode,
            company_code: counterData?.company_data?.code,
          });

          if (response?.data?.data) {
            return {
              ...item,
              ass_counter_name: response?.data?.data?.ass_counter_name,
              ass_contact_number: response?.data?.data?.ass_contact_number,
              uin_code: response?.data?.data?.uin_code,
            };
          }
          return item;
        })
      );
    }
    try {
      setIsLoading(true);
      const divisionStatuses = await initializeDivisionStatuses(
        response ? response?.data?.data : [],
        updatedDivisionCode.length > 0 ? updatedDivisionCode : divisionCode
      );
      // Check if any division requires UIN validation
      const requiresUinValidation = divisionStatuses.some((division) => division.requiresUIN && !division.isVerified && !division.isSkipped);

      if (!requiresUinValidation) {
        // If no divisions require UIN validation, proceed
        onUinVerified();
        return true;
      }

      // Find the first unverified division that requires UIN
      const nextDivisionIndex = divisionStatuses.findIndex((division: any) => division.requiresUIN && !division.isVerified && !division.isSkipped);

      if (nextDivisionIndex !== -1) {
        setAssociatedCounter(divisionStatuses[nextDivisionIndex]?.associatedCounterName);
        setMobileNumber(divisionStatuses[nextDivisionIndex]?.associatedCounterMobileNumber);
        setCurrentDivisionIndex(nextDivisionIndex);
        setUinInput(divisionStatuses[nextDivisionIndex]?.divisionData?.uin_code || "");
        setAlternateUINInput(divisionStatuses[nextDivisionIndex]?.divisionData?.company_alternate_uin || "");
        setIsOpen(true);
        return false;
      }

      return true;
    } catch (error) {
      toast({
        description: "Failed to check UIN requirement",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles verification of entered UIN for current division
   * Makes API call to validate UIN and updates state accordingly
   */
  const handleVerifyUIN = async () => {
    if (arrayOfLinkTypesForAssociatedCounter.includes(counterData?.drph_lnk_type?.dr_ph_lnk_code) && !associatedCounter) {
      setIsErrorAssociatedCounter(true);
      return;
    }
    if (arrayOfLinkTypesForAssociatedCounter.includes(counterData?.drph_lnk_type?.dr_ph_lnk_code) && mobileNumber.length !== 10) {
      setIsErrorMobileNumber(true);
      return;
    }
    try {
      if (isVerifyingUIN) return;
      setIsVerifyingUIN(true);
      const currentDivision = divisionStatuses[currentDivisionIndex];

      // Input validation
      if (!uinInput && !currentDivision.allowsAlternateUIN) {
        toast({
          description: "Please enter a UIN",
          variant: "destructive",
        });
        return;
      }

      if (currentDivision.allowsAlternateUIN && !alternateUINInput) {
        toast({
          description: "Please enter an UIN",
          variant: "destructive",
        });
        return;
      }

      // Get user token and make API call
      const token = await getCookieValue("idToken");
      const decodedToken: any = await jwtDecode((token?.toString() as string) || "");
      const response = await validateUin(
        {
          Company_DivisionCode: currentDivision.divisionCode?.toString(),
          PSR_UCode: currentDivision.psrUCode?.toString(),
          Company_UIN: currentDivision.allowsAlternateUIN ? alternateUINInput : uinInput,
          UserUCode: decodedToken["custom:ucode"],
        },
        token
      );

      if (response?.status === 200 && response?.data?.status === true) {
        setValidationResponse(response?.data);
        if (currentDivision.allowsAlternateUIN) setUinInput(response?.data?.CompanyUINDetails?.Counter_UIN);
        setShowValidationResponse(true);
        // set its status to verified
        changeDivisionVerifiedStatus(true);
        return true;
      } else {
        toast({
          description: response?.data?.message || "UIN validation failed",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      toast({
        description: "Failed to validate UIN",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsVerifyingUIN(false);
    }
  };

  /**
   * Handles skipping UIN validation for current division
   * Updates division status and proceeds to next division if available
   */
  const handleSkipDivision = () => {
    setValidationResponse(null);
    setUinInput("");
    setAlternateUINInput("");
    setDivisionStatuses((prev) => {
      const updated = [...prev];
      updated[currentDivisionIndex].isSkipped = true;
      return updated;
    });

    toast({
      description: `Skipped verification for ${divisionStatuses[currentDivisionIndex]?.divisionName}`,
      variant: "default",
    });

    // Check remaining divisions
    const remainingDivisions = divisionStatuses.slice(currentDivisionIndex + 1);
    const hasRemainingUnverifiedDivisions = remainingDivisions.some(
      (division) => division.requiresUIN && !division.isVerified && !division.isSkipped
    );

    if (!hasRemainingUnverifiedDivisions) {
      setIsOpen(false);
      onUinVerified();
    } else {
      handleNext();
    }
  };

  /**
   * Handles navigation to next division requiring UIN verification
   * @param data Optional parameter to use different division statuses than current state
   */
  const handleNext = (data?: any) => {
    const currentStatuses = data || divisionStatuses;

    // Find next unverified division
    const nextDivisionIndex = currentStatuses.findIndex(
      (division: any, index: number) => index > currentDivisionIndex && division.requiresUIN && !division.isVerified && !division.isSkipped
    );

    if (nextDivisionIndex !== -1) {
      setCurrentDivisionIndex(nextDivisionIndex);
      setIsOpen(true);
      setValidationResponse(null);
      setUinInput(currentStatuses[nextDivisionIndex]?.divisionData?.uin_code || "");
      setAlternateUINInput(currentStatuses[nextDivisionIndex]?.divisionData?.company_alternate_uin || "");
    } else {
      const hasUnverifiedDivisions = currentStatuses.some((division: any) => division.requiresUIN && !division.isVerified && !division.isSkipped);

      if (!hasUnverifiedDivisions) {
        setIsOpen(false);
        onUinVerified();
      } else {
        const firstUnverifiedIndex = currentStatuses.findIndex(
          (division: any) => division.requiresUIN && !division.isVerified && !division.isSkipped
        );
        setCurrentDivisionIndex(firstUnverifiedIndex);
        setIsOpen(true);
        setValidationResponse(null);
        setUinInput(currentStatuses[firstUnverifiedIndex]?.divisionData?.uin_code || "");
        setAlternateUINInput(currentStatuses[firstUnverifiedIndex]?.divisionData?.company_alternate_uin || "");
      }
    }
  };

  /**
   * Checks if a division requires UIN validation
   * @param divisionCode Division code to check
   * @returns Object containing UIN requirement status
   */
  // const checkUINRequirementForDivision = async (divisionCode: string) => {
  //   try {
  //     const token = await getCookieValue("idToken");
  //     const decodedToken: any = await jwtDecode((token?.toString() as string) || "");

  //     const body = {
  //       UserUCode: decodedToken["custom:ucode"],
  //       Company_DivisionCode: divisionCode?.toString(),
  //       Counter_Code: counterCode,
  //       Location_Code: counterLocationCode?.toString(),
  //     };

  //     const response = await checkUinRequired(body, token);
  //     return {
  //       isUinRequired: response?.data?.UINValidationRequired,
  //       isAlternateUinAllowed: response?.data?.AlternateUIN_Status,
  //     };
  //   } catch (error) {
  //     toast({
  //       description: "Failed to fetch UIN validation status",
  //       variant: "destructive",
  //     });
  //     return null;
  //   }
  // };

  /**
   * Initializes division statuses with UIN requirements and employee details
   * @returns Array of initialized division statuses
   */
  const initializeDivisionStatuses = async (validatedDivisions: any[], updatedDivisionCode: any[]) => {
    const initialStatuses = updatedDivisionCode.map(
      (item: { counterDivisionCode: number; expired: boolean; ass_counter_name: string; ass_contact_number: string; uin_code: string }) => {
        const code: any = item?.counterDivisionCode.toString();
        const isExpired = item?.expired;
        const divisionData = counterData?.counter_field_force_id?.find((div: any) => div?.company_division_code?.company_divisioncode == code);
        let productData = Object.values(divisionProductsData)?.find(
          (div: any) => div?.counter_ff_div_id?.company_division_code?.company_divisioncode == code || div?.alternate_uin_status == true
        );

        return {
          divisionCode: code,
          positionCode: divisionData?.position_code || "",
          isVerified:
            validatedDivisions[code] === "pending"
              ? true
              : isExpired && validatedDivisions[code] !== "pending"
                ? false
                : validatedDivisions[code] === null
                  ? false
                  : false,
          isSkipped: false,
          isExpired: isExpired,
          psrUCode: "",
          requiresUIN: true,
          allowsAlternateUIN:
            productData?.counter_ff_div_id?.company_division_code?.alternate_uin_status || productData?.alternate_uin_status || false,
          divisionName: divisionData?.company_division_code?.division_name,
          divisionData: divisionData,
          positionCodeName: "",
          associatedCounterName: item?.ass_counter_name,
          associatedCounterMobileNumber: item?.ass_contact_number,
          existingAssociatedCounterName: item?.ass_counter_name,
          existingAssociatedCounterMobileNumber: item?.ass_contact_number,
          existingUinCode: item?.uin_code,
          uin_code: item?.uin_code,
          employeeName: "",
          employeeShortCode: "",
        };
      }
    );

    try {
      // Fetch employee details for all position codes
      const {
        employeePositionCodesANdUID,
        employeePositionCodesNamesAndUID,
        employeePositionCodesNamesAndUIDWithEmployeeName,
        employeePositionCodesAndShortCode,
      } = await fetchEmployeeDetailsForPositionCode(initialStatuses.map((status) => status.positionCode));

      // Check UIN requirements and update statuses
      for (let status of initialStatuses) {
        // const uinRequirements = await checkUINRequirementForDivision(status.divisionCode);
        if (status?.allowsAlternateUIN) {
          status.requiresUIN = true;
          status.allowsAlternateUIN = true;
        } else if (status?.requiresUIN) {
          status.requiresUIN = true;
          status.allowsAlternateUIN = false;
        }
        status.psrUCode = employeePositionCodesANdUID[status.positionCode];
        status.positionCodeName = employeePositionCodesNamesAndUID[status.positionCode];
        status.employeeName = employeePositionCodesNamesAndUIDWithEmployeeName[status.positionCode];
        status.employeeShortCode = employeePositionCodesAndShortCode[status.positionCode];
      }

      setDivisionStatuses(initialStatuses);
      return initialStatuses;
    } catch (error) {
      toast({
        description: "Failed to initialize division statuses",
        variant: "destructive",
      });
      return [];
    }
  };

  /**
   * Fetches employee details for given position codes
   * @param positionCodes Array of position codes to fetch details for
   * @returns Object containing mapped employee details
   */
  const fetchEmployeeDetailsForPositionCode = async (positionCodes: string[]) => {
    const response = await queryEmployeeDetails(`/details?position_code=${positionCodes}`);

    const employeePositionCodesANdUID: { [key: string]: string } = {};
    const employeePositionCodesNamesAndUID: { [key: string]: string } = {};
    const employeePositionCodesNamesAndUIDWithEmployeeName: { [key: string]: string } = {};
    const employeePositionCodesAndShortCode: { [key: string]: string } = {};
    response?.data?.data?.forEach((item: any) => {
      employeePositionCodesANdUID[item?.emp_position_hdr?.position_code] = item?.uid;
      employeePositionCodesNamesAndUID[item?.emp_position_hdr?.position_code] = item?.emp_position_hdr?.position_code_name;
      employeePositionCodesNamesAndUIDWithEmployeeName[item?.emp_position_hdr?.position_code] = item?.emp_name;
      employeePositionCodesAndShortCode[item?.emp_position_hdr?.position_code] = item?.emp_shortcode;
    });

    return {
      employeePositionCodesANdUID,
      employeePositionCodesNamesAndUID,
      employeePositionCodesNamesAndUIDWithEmployeeName,
      employeePositionCodesAndShortCode,
    };
  };

  const handleUpdateDivisionDetails = async () => {
    if (
      arrayOfLinkTypesForAssociatedCounter.includes(counterData?.drph_lnk_type?.dr_ph_lnk_code) &&
      divisionStatuses[currentDivisionIndex]?.existingAssociatedCounterName === associatedCounter &&
      divisionStatuses[currentDivisionIndex]?.existingAssociatedCounterMobileNumber === mobileNumber &&
      divisionStatuses[currentDivisionIndex]?.existingUinCode === uinInput
    ) {
      updateDivisionDetails();
    } else if (arrayOfLinkTypesForAssociatedCounter.includes(counterData?.drph_lnk_type?.dr_ph_lnk_code)) {
      updateIndphDetails();
    } else {
      updateDivisionDetails();
    }
  };

  /**
   * Updates division details after successful UIN validation
   * Makes API call to update division data
   */
  const updateDivisionDetails = async () => {
    try {
      if (isUpdatingDivision) return;
      setIsUpdatingDivision(true);
      const currentDivision = divisionStatuses[currentDivisionIndex];

      const associatedCounterData = {
        counter_id: currentDivision?.divisionData?.assc_counterhdr_id?.counter_id,
        counter_code: currentDivision?.divisionData?.Associated_Counter_Code,
        location_code: currentDivision?.divisionData?.Associated_Location_Code,
      };
      const payload = {
        company_division_code: [
          {
            id: currentDivision?.divisionData?.id,
            counter_id: currentDivision?.divisionData?.counter_id,
            active_status: currentDivision?.divisionData?.active_status,
            counter_company_lnk_id: counterData?.counter_company_location_id,
            company_division_code: currentDivision?.divisionData?.company_division_code?.company_divisioncode,
            company_division_name: currentDivision?.divisionData?.company_division_code?.division_name,
            position_code: currentDivision?.divisionData?.position_code,
          },
        ],
        counter_company_lnk_id: counterData?.counter_company_location_id,
        counter_id: counterData?.counter_id,
        counter_code: counterData?.counter_location?.counter_hdr_id?.counter_code,
        reference_number: currentDivision?.divisionData?.reference_number || "",
        require_uin_status: currentDivision?.requiresUIN,
        uin_verify_skip: currentDivision?.isSkipped,
        verify_hq: false,
        apitaken: false,
        uin_last_verified_date: new Date(),
        company_code: counterData?.company_data?.code,
        company_name: counterData?.company_data?.name,
        location_id: counterData?.counter_location?.counter_location_id,
        location_name: counterData?.counter_location?.location_name,
        location_code: counterData?.counter_location?.location_code,
        max_discount_perc: currentDivision?.divisionData?.max_discount_perc,
        assc_counterhdr_id: associatedCounterData.counter_id,
        company_alternate_uin: validationResponse?.CompanyUINDetails?.Counter_AlternateUIN,
        uin_code: validationResponse?.CompanyUINDetails?.Counter_UIN,
        position_code_name: currentDivision?.positionCodeName || null,
        uin_exemption: currentDivision?.divisionData?.uin_exemption,
        ewaybill_applicable: currentDivision?.divisionData?.ewaybill_applicable || "N",
        Associated_Counter_Code: associatedCounterData.counter_code,
        Associated_Location_Code: associatedCounterData.location_code,
        counter_type_id: divisionStatuses[currentDivisionIndex]?.divisionData?.counterffdivision_lnk_id?.map((item: any) => {
          if (item.counter_ff_division_module_lnk_id)
            return {
              counter_ff_division_module_lnk_id: item.counter_ff_division_module_lnk_id,
              module_code: item.module_code?.[0]?.module_code,
              active_status: item.active_status,
            };
          else {
            return {
              module_code: item.module_code?.[0]?.module_code,
              active_status: item.active_status,
            };
          }
        }),
        cmp_countertag_id: counterData?.counter_tag?.cmp_countertag_id || null,
        u_code: currentDivision?.divisionData?.u_code,
        counter_ff_div_ucode: currentDivision?.divisionData?.Ucode,
      };
      const res = await EditDivision(payload, counterData?.counter_id);
      if (res?.status !== 200) throw new Error(res?.data?.message);
      toast({
        description: "Division details updated successfully",
      });
      setAssociatedCounter("");
      setMobileNumber("");
      setUinInput("");
      setValidationResponse(null);
      handleSkipDivision();
    } catch (error: any) {
      console.log(error);
      toast({
        description: error?.message || "Failed to update division details",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingDivision(false);
    }
  };
  const updateIndphDetails = async () => {
    if (!associatedCounter || associatedCounter.length === 0) {
      toast({
        description: "Associated Counter is required",
        variant: "destructive",
      });
      setIsErrorAssociatedCounter(true);
      return;
    }
    if (mobileNumber.length !== 10 || !mobileNumber) {
      toast({
        description: "Mobile Number is required",
        variant: "destructive",
      });
      setIsErrorMobileNumber(true);
      return;
    }
    try {
      if (isUpdatingDivision) return;
      setIsUpdatingDivision(true);
      const currentDivision = divisionStatuses[currentDivisionIndex];
      const associatedCounterData = {
        counter_id: currentDivision?.divisionData?.assc_counterhdr_id?.counter_id,
        counter_code: currentDivision?.divisionData?.Associated_Counter_Code,
        uin_verify_skip: currentDivision?.divisionData?.uin_verify_skip,
        company_alternate_uin: currentDivision?.divisionData?.company_alternate_uin,
        assc_counterhdr_id: currentDivision?.divisionData?.assc_counterhdr_id?.counter_id,
      };
      const payload = {
        data: {
          source_code: orderCode || "",
          source_type: "order",
          status: "pending",
          created_by: "user_001",
          modified_by: "user_001",
          actor_remarks: "Approved for processing",
          company_code: counterData?.company_data?.code,
          alternate_uin_required: true,
          ass_counter_name: associatedCounter,
          ass_contact_number: mobileNumber,
          alternate_uin_code: validationResponse?.CompanyUINDetails?.Counter_AlternateUIN,
          uin_code: validationResponse?.CompanyUINDetails?.Counter_UIN,
          uin_state: validationResponse?.CompanyUINDetails?.State,
          uin_city: validationResponse?.CompanyUINDetails?.City,
          uin_division: validationResponse?.CompanyUINDetails?.Division_Speciality,
          uin_speciality: validationResponse?.CompanyUINDetails?.UIN_Speciality,
          company_divisioncode: currentDivision?.divisionCode,
          counter_code: counterCode,
          counter_id: counterData?.counter_id,
          Associated_Counter_Code: associatedCounterData?.counter_code,
          assc_counterhdr_id: associatedCounterData?.assc_counterhdr_id,
          company_alternate_uin: associatedCounterData?.company_alternate_uin,
          uin_verify_skip: associatedCounterData?.uin_verify_skip,
          counterffdivision_lnk_id: currentDivision?.divisionData?.id,
        },
        primary_key: counterData?.counter_id,
        event_type: "COUNTER_INDPH_UIN_REQUEST",
      };
      const response = await updateIndphUinValidation(payload);
      if (response?.status !== 200) throw new Error(response?.data?.message);
      toast({
        description: "UIN validation updated successfully",
      });
      setAssociatedCounter("");
      setMobileNumber("");
      setUinInput("");
      setValidationResponse(null);
      handleSkipDivision();
    } catch (error: any) {
      console.log(error);
      toast({
        description: error?.response?.data?.message || "Failed to update UIN validation",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingDivision(false);
    }
  };

  const changeDivisionVerifiedStatus = (status: boolean) => {
    setDivisionStatuses((prev) => {
      const updated = [...prev];
      updated[currentDivisionIndex].isVerified = status;
      return updated;
    });
  };

  const arrayOfLinkTypesForAssociatedCounter = ["INDPH-HS", "INDPH", "INDPH-PH"];

  /**
   * Modal component for UIN validation workflow
   */
  const modal = (
    <Modal
      isOpen={isOpen}
      isHeader={true}
      title={`${divisionStatuses[currentDivisionIndex]?.divisionName}`}
      onCloseModal={() => {
        setDivisionStatuses([]);
        setShowValidationResponse(false);
        setIsOpen(false);
      }}
    >
      {!showValidationResponse && (
        <div className="p-6 animate-fade-in">
          <div className="flex gap-4 flex-col mb-4">
            <div className="border-b border-gray-200 pb-4 mb-4">
              <div className="flex justify-between items-center text-sm font-medium text-gray-600">
                <span className="flex items-center gap-1">FF Name</span>
                <span className="font-semibold text-gray-800 bg-gray-50 px-3 py-1 rounded-lg">
                  {divisionStatuses[currentDivisionIndex]?.employeeName || "-"}
                  {divisionStatuses[currentDivisionIndex]?.employeeShortCode && (
                    <span className="text-xs text-gray-500">({divisionStatuses[currentDivisionIndex]?.employeeShortCode})</span>
                  )}
                </span>
              </div>
            </div>
            {arrayOfLinkTypesForAssociatedCounter.includes(counterData?.drph_lnk_type?.dr_ph_lnk_code) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Associated Counter <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Associated Counter"
                    onBlur={(e) => {
                      setAssociatedCounter(e.target.value || "");
                      setIsErrorAssociatedCounter(false);
                      setDivisionStatuses((prev) => {
                        const updated = [...prev];
                        updated[currentDivisionIndex].associatedCounterName = e.target.value;
                        return updated;
                      });
                    }}
                    isError={isErrorAssociatedCounter}
                    errorMessage="Associated Counter is required"
                    value={divisionStatuses[currentDivisionIndex]?.associatedCounterName || associatedCounter}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Mobile Number"
                    onBlur={(e) => {
                      setMobileNumber(e.target.value || "");
                      setIsErrorMobileNumber(false);
                      setDivisionStatuses((prev) => {
                        const updated = [...prev];
                        updated[currentDivisionIndex].associatedCounterMobileNumber = e.target.value;
                        return updated;
                      });
                    }}
                    type="number"
                    value={divisionStatuses[currentDivisionIndex]?.associatedCounterMobileNumber || mobileNumber}
                    isError={isErrorMobileNumber}
                    errorMessage={
                      !mobileNumber
                        ? "Mobile Number is required"
                        : mobileNumber.length > 10
                          ? "Mobile Number must be 10 digits"
                          : mobileNumber.length < 10
                            ? "Mobile Number must be 10 digits"
                            : ""
                    }
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UIN</label>
              <Input
                type="text"
                className="w-full p-2 border rounded-md transition-all duration-200"
                placeholder="Enter UIN for verification"
                onChange={(e) => {
                  setUinInput(e.target.value);
                  changeDivisionVerifiedStatus(false);
                  setDivisionStatuses((prev) => {
                    const updated = [...prev];
                    updated[currentDivisionIndex].uin_code = e.target.value;
                    return updated;
                  });
                }}
                value={divisionStatuses[currentDivisionIndex]?.uin_code || uinInput}
                disabled={divisionStatuses[currentDivisionIndex]?.allowsAlternateUIN}
              />
            </div>
            {divisionStatuses[currentDivisionIndex]?.allowsAlternateUIN && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt. UIN</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md transition-all duration-200"
                  placeholder="Enter UIN for verification"
                  onChange={(e) => {
                    setAlternateUINInput(e.target.value);
                    changeDivisionVerifiedStatus(false);
                  }}
                  value={alternateUINInput}
                />
              </div>
            )}
          </div>

          <div className="flex justify-between mt-4">
            <Button loaderSize="small" isLoading={isUpdatingDivision} onClick={handleSkipDivision}>
              Skip Division
            </Button>

            <Button
              loaderSize="small"
              isLoading={isUpdatingDivision || isVerifyingUIN}
              onClick={divisionStatuses[currentDivisionIndex]?.isVerified ? handleUpdateDivisionDetails : handleVerifyUIN}
              className="bg-primary text-white"
            >
              {divisionStatuses[currentDivisionIndex]?.isVerified ? "Update UIN" : "Verify UIN"}
            </Button>
          </div>
        </div>
      )}

      {showValidationResponse && (
        <UinOrderDetailsPopup
          divisionData={divisionStatuses[currentDivisionIndex]?.divisionData}
          onConfirmLoading={isUpdatingDivision}
          uinData={validationResponse}
          onConfirm={() => setShowValidationResponse(false)}
          associatedCounterName={associatedCounter}
        />
      )}
    </Modal>
  );

  return {
    modal,
    isOpen,
    checkUINValidation,
    divisionStatuses,
    isLoading,
  };
};

export default useOrderUinConfirmation;
